import * as bootstrap from 'bootstrap';
import { get, post, notifier } from '../util';
import * as Highcharts from 'highcharts';

// Module-level state
let currentDate: string = '';
let smConverting = false;  // 4-way sync guard (stairmaster cards)
let newCardCounter = 0;
let hiddenListenerAttached = false;

// Stairmaster conversion constants
const STEPS_PER_FLOOR = 10;
const KM_PER_STEP = 0.0003048;
const KM_MILES = 1.60934;

interface StairmasterData {
    stairmaster_id: number;
    floors: number | null;
    steps: number | null;
    km: number | null;
    miles: number | null;
    time: string | null;
}

function formatModalDate(date: string): string {
    return new Date(date + 'T00:00:00').toLocaleString('en', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function openStairmasterModal(date: string): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    currentDate = date;
    newCardCounter = 0;

    if (!hiddenListenerAttached) {
        modalEl.addEventListener('hidden.bs.modal', () => {
            const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
            if (footer) footer.classList.remove('d-none');
            currentDate = '';
        });
        hiddenListenerAttached = true;
    }

    get('?do=getStairmasterFromDate&date=' + date, {}, (data: any) => {
        const smData: StairmasterData[] = data.status === 1 ? (data.sessions || []) : [];

        const title = document.getElementById('chart-edit-modal-title');
        if (title) {
            title.textContent = 'Stairmaster \u2014 ' + formatModalDate(date);
        }

        const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
        if (footer) footer.classList.add('d-none');

        const body = document.getElementById('chart-edit-modal-body');
        if (!body) return;
        body.innerHTML = '';

        // Container for all cards
        const container = document.createElement('div');
        container.className = 'activity-cards-container';
        body.appendChild(container);

        // Render existing stairmaster cards
        for (const session of smData) {
            renderSmCard(container, session);
        }

        // Add button row — stairmaster only
        const addRow = document.createElement('div');
        addRow.className = 'd-flex gap-2 mt-2';

        const addSmBtn = document.createElement('button');
        addSmBtn.type = 'button';
        addSmBtn.className = 'btn btn-outline-warning flex-fill';
        addSmBtn.textContent = '+ Stairmaster';
        addSmBtn.addEventListener('click', () => {
            const card = renderSmCard(container, null);
            container.appendChild(card);
            card.scrollIntoView({ behavior: 'smooth' });
        });

        addRow.appendChild(addSmBtn);
        body.appendChild(addRow);

        // Save All button
        const saveAllBtn = document.createElement('button');
        saveAllBtn.type = 'button';
        saveAllBtn.className = 'btn btn-primary w-100 mt-2 js-activity-save-all';
        saveAllBtn.innerHTML = '<span class="js-activity-save-label">Save All</span><span class="spinner-border spinner-border-sm d-none js-activity-spinner" role="status"></span>';
        saveAllBtn.addEventListener('click', () => handleSaveAll(saveAllBtn));
        body.appendChild(saveAllBtn);

        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl, { backdrop: true, keyboard: true });
        modalInstance.show();
    });
}

// ── Stairmaster cards ──────────────────────────────────────

function renderSmCard(container: HTMLElement, session: StairmasterData | null): HTMLElement {
    const cardId = session ? 'sm-card-' + session.stairmaster_id : 'sm-card-new-' + (++newCardCounter);

    const card = document.createElement('div');
    card.className = 'card mb-3 sm-card';
    card.id = cardId;
    card.dataset.smId = session ? String(session.stairmaster_id) : '';
    card.dataset.type = 'stairmaster';

    const header = document.createElement('div');
    header.className = 'card-header d-flex justify-content-between align-items-center py-1 px-2';
    header.innerHTML = `<small class="fw-semibold" style="color:#FF8C00">Stairmaster</small><button type="button" class="btn btn-sm btn-link text-danger p-0 js-card-delete" title="Delete">\u{1F5D1}</button>`;
    card.appendChild(header);

    const cardBody = document.createElement('div');
    cardBody.className = 'card-body py-2 px-2';

    const floorsVal = session?.floors != null ? String(session.floors) : '';
    const stepsVal  = session?.steps  != null ? String(session.steps)  : '';
    const kmVal     = session?.km     != null ? String(session.km)     : '';
    const milesVal  = session?.miles  != null ? String(session.miles)  : '';
    const timeVal   = session?.time ?? '';

    cardBody.innerHTML = `
        <div class="row g-2 mb-2">
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">floors</label>
                <input type="number" step="1" min="0" inputmode="numeric" class="form-control form-control-sm js-sm-floors" value="${floorsVal}" />
            </div>
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">steps</label>
                <input type="number" step="1" min="0" inputmode="numeric" class="form-control form-control-sm js-sm-steps" value="${stepsVal}" />
            </div>
        </div>
        <div class="row g-2 mb-2">
            <div class="col-6">
                <label class="form-label form-label-sm mb-1 js-sm-km-label">km</label>
                <input type="number" step="any" inputmode="decimal" class="form-control form-control-sm js-sm-km" value="${kmVal}" />
            </div>
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">miles</label>
                <input type="number" step="any" inputmode="decimal" class="form-control form-control-sm js-sm-miles" value="${milesVal}" />
            </div>
        </div>
        <div class="row g-2">
            <div class="col-4">
                <label class="form-label form-label-sm mb-1">time</label>
                <input type="text" class="form-control form-control-sm js-sm-time" placeholder="HH:MM:SS" value="${timeVal}" />
            </div>
        </div>
    `;
    card.appendChild(cardBody);

    // Attach 4-way sync (floors/steps/km/miles)
    const floorsInput = card.querySelector('.js-sm-floors') as HTMLInputElement;
    const stepsInput  = card.querySelector('.js-sm-steps')  as HTMLInputElement;
    const kmInput     = card.querySelector('.js-sm-km')     as HTMLInputElement;
    const milesInput  = card.querySelector('.js-sm-miles')  as HTMLInputElement;
    const kmLabel     = card.querySelector('.js-sm-km-label') as HTMLElement;
    attachSmSync(floorsInput, stepsInput, kmInput, milesInput, kmLabel);

    // Set initial ~km label when session has floors/steps and km (likely auto-calculated)
    if ((session?.floors != null || session?.steps != null) && session?.km != null) {
        kmLabel.textContent = '~km';
    }

    const deleteBtn = header.querySelector('.js-card-delete') as HTMLButtonElement;
    deleteBtn.addEventListener('click', () => handleDeleteSm(card, session));

    container.appendChild(card);
    return card;
}

function attachSmSync(
    floorsInput: HTMLInputElement,
    stepsInput: HTMLInputElement,
    kmInput: HTMLInputElement,
    milesInput: HTMLInputElement,
    kmLabel: HTMLElement
): void {
    floorsInput.addEventListener('input', () => {
        if (smConverting) return;
        smConverting = true;
        const f = parseFloat(floorsInput.value);
        if (!isNaN(f) && floorsInput.value !== '') {
            stepsInput.value = String(Math.round(f * STEPS_PER_FLOOR));
            const km = (f * STEPS_PER_FLOOR * KM_PER_STEP).toFixed(3);
            kmInput.value = km;
            milesInput.value = (parseFloat(km) / KM_MILES).toFixed(3);
            kmLabel.textContent = '~km';
        } else {
            stepsInput.value = '';
            kmInput.value = '';
            milesInput.value = '';
            kmLabel.textContent = '~km';
        }
        smConverting = false;
    });

    stepsInput.addEventListener('input', () => {
        if (smConverting) return;
        smConverting = true;
        const s = parseFloat(stepsInput.value);
        if (!isNaN(s) && stepsInput.value !== '') {
            floorsInput.value = String(Math.round(s / STEPS_PER_FLOOR));
            const km = (s * KM_PER_STEP).toFixed(3);
            kmInput.value = km;
            milesInput.value = (parseFloat(km) / KM_MILES).toFixed(3);
            kmLabel.textContent = '~km';
        } else {
            floorsInput.value = '';
            kmInput.value = '';
            milesInput.value = '';
            kmLabel.textContent = '~km';
        }
        smConverting = false;
    });

    kmInput.addEventListener('input', () => {
        if (smConverting) return;
        smConverting = true;
        const km = parseFloat(kmInput.value);
        milesInput.value = !isNaN(km) && kmInput.value !== '' ? (km / KM_MILES).toFixed(3) : '';
        floorsInput.value = '';
        stepsInput.value = '';
        kmLabel.textContent = 'km';
        smConverting = false;
    });

    milesInput.addEventListener('input', () => {
        if (smConverting) return;
        smConverting = true;
        const miles = parseFloat(milesInput.value);
        kmInput.value = !isNaN(miles) && milesInput.value !== '' ? (miles * KM_MILES).toFixed(3) : '';
        floorsInput.value = '';
        stepsInput.value = '';
        kmLabel.textContent = 'km';
        smConverting = false;
    });
}

function handleDeleteSm(card: HTMLElement, session: StairmasterData | null): void {
    if (!window.confirm('Delete this session?')) return;

    if (!session || !session.stairmaster_id) {
        card.remove();
        return;
    }

    post('?do=deleteStairmaster&stairmasterId=' + session.stairmaster_id, {}, (data: any) => {
        if (data.status === 1) {
            card.remove();
            if (data.new_steps_sum !== undefined) {
                updateStairmasterChartPoint(currentDate, data.new_steps_sum);
            }
            notifier.success('Session deleted');
        } else {
            alert(data.message || 'Failed to delete session.');
        }
    });
}

// ── Save All ───────────────────────────────────────────────

function handleSaveAll(saveAllBtn: HTMLButtonElement): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    const saveLabel = saveAllBtn.querySelector('.js-activity-save-label') as HTMLElement;
    const spinner = saveAllBtn.querySelector('.js-activity-spinner') as HTMLElement;

    saveAllBtn.disabled = true;
    saveLabel.classList.add('d-none');
    spinner.classList.remove('d-none');

    const prevError = modalEl.querySelector('.activity-inline-error');
    if (prevError) prevError.remove();

    const smCards = Array.from(document.querySelectorAll('#chart-edit-modal-body .sm-card')) as HTMLElement[];

    if (smCards.length === 0) {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');
        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
        modalInstance.hide();
        return;
    }

    const smPromises: Promise<any>[] = smCards.map(card => {
        const smIdAttr = card.dataset.smId || '';
        const stairmasterId = smIdAttr !== '' ? parseInt(smIdAttr) : null;
        const payload: Record<string, string | number> = {
            floors: (card.querySelector('.js-sm-floors') as HTMLInputElement).value,
            steps:  (card.querySelector('.js-sm-steps')  as HTMLInputElement).value,
            km:     (card.querySelector('.js-sm-km')     as HTMLInputElement).value,
            miles:  (card.querySelector('.js-sm-miles')  as HTMLInputElement).value,
            time:   (card.querySelector('.js-sm-time')   as HTMLInputElement).value,
        };
        if (stairmasterId !== null && stairmasterId > 0) payload.stairmaster_id = stairmasterId;
        return new Promise<any>((resolve, reject) => {
            post('?do=upsertStairmaster&date=' + currentDate, payload, (data: any) => {
                data.status === 1 ? resolve(data) : reject(data.message || 'Save failed');
            });
        });
    });

    Promise.all(smPromises).then(results => {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        // Update stairmaster chart point from last sm result
        const lastSm = results[results.length - 1];
        if (lastSm?.new_steps_sum !== undefined) {
            updateStairmasterChartPoint(currentDate, lastSm.new_steps_sum);
        }

        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
        modalInstance.hide();
        notifier.success('Saved');
    }).catch(errMsg => {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        const body = document.getElementById('chart-edit-modal-body');
        if (body) {
            const errorEl = document.createElement('div');
            errorEl.className = 'text-danger small mt-2 activity-inline-error';
            errorEl.textContent = typeof errMsg === 'string' ? errMsg : 'An error occurred. Please try again.';
            body.appendChild(errorEl);
        }
    });
}

// ── Chart update ───────────────────────────────────────────

function updateStairmasterChartPoint(date: string, newStepsSum: number | null): void {
    //@ts-ignore
    const chart = (Highcharts.charts as (Highcharts.Chart | undefined)[]).find((c: any) => c?.renderTo?.id === 'chart_stairmaster');
    if (!chart) return;

    const ts = Date.parse(date + 'T00:00:00');
    const series = chart.series[0];
    if (!series) return;

    const point = series.points.find((p: Highcharts.Point) => p.x === ts);
    if (point) {
        //@ts-ignore
        point.update(newStepsSum);
    }
}
